import { CSSProperties } from "react";
import styled from "@emotion/styled";
import { GrButton, GrChip, GrIconButton, GrStack, GrTypography } from "@jnpmedi/grebob";
import { AuditTrailViewType } from "@newComponents/common/SignerAuditTrail";
import { MvnMessage } from "@newLibs/utils";
import { useSectionFloodDetect } from "@newHooks/common/useSectionFloodDetect";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { DocumentSignReferrerSummary } from "@maven-rest/docs/DocumentSignReferrer.dto";

interface CommonReferrerViewSectionProps {
  referrers: Array<DocumentSignReferrerSummary>;
  viewType?: AuditTrailViewType;
  containerStyle?: CSSProperties;
}

const SINGLE_REFERRER_LINE_HEIGHT = "20px";

export function CommonReferrerViewSection({
  referrers,
  viewType = AuditTrailViewType.Detail,
  containerStyle,
}: CommonReferrerViewSectionProps) {
  const { isMoreViewOpened, isSectionFlooded, divContainerRef, onMoreViewOpenChange } = useSectionFloodDetect({
    itemClassName: ".referrer-item",
    singleLineHeight: SINGLE_REFERRER_LINE_HEIGHT,
  });

  if (!referrers.length) {
    return null;
  }

  return (
    <GrStack style={containerStyle}>
      <GrStack gap={8}>
        <GrTypography style={{ minWidth: "fit-content" }} type="Content.Subtitle04" color="grey050">
          {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_STATUS_REFERRER_LABEL)}
        </GrTypography>

        <ReferrerStackWrapper ref={divContainerRef}>
          {referrers.map((referrer) => (
            <GrChip
              className="referrer-item"
              key={String(referrer.id)}
              size="small"
              color="grey"
              variant="soft"
              shape="circle"
              style={{ minWidth: "fit-content" }}
              label={resolveReferrerChipTitle(viewType, referrer)}
            />
          ))}

          {/* 숨기기 버튼 */}
          {isSectionFlooded.value && (
            <GrButton
              size="small"
              variant="text"
              style={{ padding: 0, margin: 0, minWidth: "fit-content", height: "fit-content" }}
              onClick={() => onMoreViewOpenChange(false)}
            >
              <GrTypography type="Content.Caption02" color="maven-blue-dark">
                {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_STATUS_REFERRER_HIDE_TEXT_BUTTON)}
              </GrTypography>
            </GrButton>
          )}

          {/* 더보기 닫혀있을 때 햄버거 버튼 */}
          {isSectionFlooded.value && !isMoreViewOpened.value && (
            <GrIconButton
              style={{ position: "absolute", top: 0, right: 0, padding: 4 }}
              iconType="dot-horizontal"
              size={12}
              onClick={() => onMoreViewOpenChange(true)}
            />
          )}
        </ReferrerStackWrapper>
      </GrStack>
    </GrStack>
  );
}

function resolveReferrerChipTitle(viewType: AuditTrailViewType, referrer: DocumentSignReferrerSummary) {
  if (viewType === AuditTrailViewType.Detail) {
    return `${referrer.name} (${referrer.email})`;
  }

  return referrer.name;
}

const ReferrerStackWrapper = styled(GrStack)`
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 8px 4px;
  overflow: hidden;
  transition: max-height 0.3s ease;
`;
